import React, { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import "../styles/auth.css";

// ── Documents requis par profil ────────────────────────────────────────────
const DOCS_PRO = [
  { id: "CNI",                name: "Pièce d'identité du gérant",             sub: "CNI ou passeport — KYC",                        req: true },
  { id: "KBIS",               name: "Extrait Kbis",                           sub: "Moins de 3 mois",                               req: true },
  { id: "URSSAF",             name: "Attestation de vigilance URSSAF",        sub: "Moins de 6 mois",                               req: true },
  { id: "RIB",                name: "RIB / IBAN",                             sub: "Pour vos reversements Stripe Connect",          req: true },
  { id: "PERMIS",             name: "Permis de conduire",                     sub: "Catégorie adaptée au véhicule — date d'expiration", req: true, expiry: true },
  { id: "CQC",                name: "Carte de Qualification Conducteur (CQC)",sub: "Véhicules > 3,5 t — date d'expiration",         req: true, expiry: true },
  { id: "LTI",                name: "Licence de Transport Intérieur (LTI)",   sub: "Obligatoire transport onéreux — date d'expiration", req: true, expiry: true },
  { id: "RC_PRO",             name: "Assurance RC Professionnelle",           sub: "Attestation en cours de validité",              req: true, expiry: true },
  { id: "ASSURANCE_VEHICULE", name: "Assurance du véhicule professionnel",    sub: "Plateau / dépanneuse",                          req: true, expiry: true },
  { id: "CT",                 name: "Contrôle technique",                     sub: "En cours de validité",                          req: true, expiry: true },
  { id: "AUTORISATION_PREF",  name: "Autorisation préfectorale de dépannage", sub: "Le cas échéant (interventions réseau rapide)",  req: false, cond: true },
];

const PCTS = [5, 28, 55, 80, 100];

const STEPS = [
  { label: "Profil",                  sub: "Client ou transporteur" },
  { label: "Informations",            sub: "Données personnelles / pro" },
  { label: "Pièces justificatives",   sub: "Upload des documents requis" },
  { label: "Validation",              sub: "CGU et création du compte" },
];

// ── Icônes SVG inline ────────────────────────────────────────────
const IcArrow = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
const IcBack  = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
const IcCheck = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IcFile  = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
const IcUpload= () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;
const IcWarn  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
const IcShield= () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const IcClock = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IcEye   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>;
const IcEyeOff= () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;

// ── Left panel avec stepper ───────────────────────────────────────
function LeftPanel({ step }) {
  return (
    <div className="auth-left">
      <div className="auth-logo">
        <div className="auth-logo-mark">
          <svg viewBox="0 0 24 24" fill="none" stroke="#0E0F0D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
            <rect x="1" y="3" width="15" height="13" rx="2"/>
            <path d="M16 8h4l3 5v3h-7V8z"/>
            <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
          </svg>
        </div>
        <div>
          <div className="auth-logo-name">DepannRoute</div>
          <div className="auth-logo-tag">Inscription nouveau compte</div>
        </div>
      </div>

      <div className="auth-step-nav">
        <div className="auth-step-nav-title">Étapes d'inscription</div>
        {STEPS.map((s, i) => {
          const cls = i < step ? "sn-done" : i === step ? "sn-active" : "";
          return (
            <div key={i} className={`auth-snav-item ${cls}`}>
              <div className="auth-snav-dot">
                {i < step ? <IcCheck /> : i + 1}
              </div>
              <div>
                <div className="auth-snav-label">{s.label}</div>
                <div className="auth-snav-sub">{s.sub}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="auth-left-bottom">
        <div className="auth-blockchain">
          <IcShield/>
          <div className="auth-blockchain-text">
            <strong>Documents scellés SHA-256</strong>
            Empreinte cryptographique et horodatage à chaque dépôt
          </div>
        </div>
      </div>

      <div className="auth-road">
        <svg viewBox="0 0 500 300" preserveAspectRatio="xMidYMax slice" fill="none">
          <path d="M0 280 Q125 200 250 220 Q375 240 500 160" stroke="rgba(232,160,32,.15)" strokeWidth="60"/>
          <path d="M0 280 Q125 200 250 220 Q375 240 500 160" stroke="rgba(232,160,32,.06)" strokeWidth="120"/>
          <path d="M0 280 Q125 200 250 220 Q375 240 500 160" stroke="rgba(232,160,32,.3)" strokeWidth="1.5" strokeDasharray="18 12"/>
        </svg>
      </div>
    </div>
  );
}

// ── Composant principal ─────────────────────────────────────────────
export default function Register() {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(params.get("role") === "pro" ? "pro" : null);
  const [form, setForm]   = useState({ prenom:"", nom:"", email:"", tel:"", adresse:"", cp:"", ville:"", pwd:"", pwd2:"", forme:"", siret:"", raison:"" });
  const [uploaded, setUploaded] = useState({});
  const [cguChecked, setCguChecked] = useState(false);
  const [pwdVisible, setPwdVisible] = useState(false);
  const [docsSent, setDocsSent]     = useState(0);   // documents transmis à la Conformité
  const [docErrors, setDocErrors]   = useState(0);   // transferts en échec (à refaire depuis l'espace)
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);

  const upd = k => e => setForm(f => ({...f, [k]: e.target.value}));
  const docs = DOCS_PRO; // l'étape documents n'existe que pour les transporteurs
  const reqDocs = docs.filter(d => d.req);
  const condDocs = docs.filter(d => !d.req);
  const allDocsOk = reqDocs.every(d => uploaded[d.id]);

  // — Upload fichier
  const handleFileChange = (docId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploaded(u => ({...u, [docId]: file}));
  };

  // — Soumission finale : création du compte PUIS transmission réelle des
  // documents à l'espace Conformité (vérification par l'équipe)
  const finalSubmit = async () => {
    setError("");
    setLoading(true);
    try {
      const name = [form.prenom, form.nom].filter(Boolean).join(" ");
      const payload = {
        email: form.email,
        password: form.pwd,
        name,
        phone: form.tel,
        company: form.raison || "",
        siret: form.siret || null,
        city: form.ville,
        country: "FR",
        role: profile,
      };
      const { data } = await api.post("/auth/register", payload);
      setUser(data);

      if (profile === "pro") {
        let sent = 0, failed = 0;
        for (const [docType, file] of Object.entries(uploaded)) {
          const fd = new FormData();
          fd.append("doc_type", docType);
          fd.append("file", file, file.name);
          try {
            await api.post("/pro/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            sent += 1;
          } catch {
            failed += 1;
          }
        }
        setDocsSent(sent);
        setDocErrors(failed);
      }
      setStep(4); // success
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // — Validation étape 1 (les clients n'ont pas de dossier documentaire :
  // ils passent directement à la validation)
  const validateStep1 = () => {
    if (!form.prenom || !form.nom || !form.email || !form.pwd) { setError("Veuillez remplir tous les champs obligatoires."); return; }
    if (form.pwd.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (form.pwd !== form.pwd2) { setError("Les mots de passe ne correspondent pas."); return; }
    setError(""); setStep(profile === "pro" ? 2 : 3);
  };

  const pct = PCTS[Math.min(step, 4)];

  // ── RENDER STEPS ─────────────────────────────────────────────

  // ── Étape 0 : choix profil
  const renderStep0 = () => (
    <div className="auth-pane">
      <div className="auth-step-header">
        <div className="auth-step-tag"><span className="auth-step-tag-dot"/>Étape 1 sur 4</div>
        <h2 className="auth-step-title">Choisissez votre profil</h2>
        <p className="auth-step-sub">Votre espace et vos documents requis varient selon votre statut.</p>
      </div>
      <div className="auth-profile-grid">
        <div className={`auth-profile-card auth-profile-client${profile==="client"?" pcard-active":""}`}
             onClick={() => setProfile("client")} data-testid="role-client">
          <div className="auth-profile-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <h3>Client particulier</h3>
          <p>Je souhaite faire transporter mon véhicule via la plateforme.</p>
        </div>
        <div className={`auth-profile-card auth-profile-pro${profile==="pro"?" pcard-active":""}`}
             onClick={() => setProfile("pro")} data-testid="role-pro">
          <div className="auth-profile-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
          </div>
          <h3>Transporteur professionnel</h3>
          <p>Je propose des services de transport et de dépannage.</p>
        </div>
      </div>
      <div className="auth-btn-row">
        <button className="auth-btn-submit" onClick={() => { setError(""); setStep(1); }} disabled={!profile} data-testid="next-step-0">
          <span>Continuer</span><div className="auth-btn-arrow"><IcArrow/></div>
        </button>
      </div>
    </div>
  );

  // ── Étape 1 : informations
  const renderStep1 = () => (
    <div className="auth-pane">
      <div className="auth-step-header">
        <div className="auth-step-tag"><span className="auth-step-tag-dot"/>Étape 2 sur 4</div>
        <h2 className="auth-step-title">{profile==="pro"?"Informations professionnelles":"Informations personnelles"}</h2>
        <p className="auth-step-sub">Toutes les données sont chiffrées et conformes au RGPD.</p>
      </div>

      {error && <div className="auth-alert auth-alert-error"><IcWarn/> {error}</div>}

      <div className="auth-grid-2">
        <div className="auth-field-group">
          <label className="auth-field-label" htmlFor="prenom">Prénom <span>*</span></label>
          <div className="auth-field-wrap">
            <input type="text" id="prenom" placeholder="Jean" value={form.prenom} onChange={upd("prenom")} required/>
            <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>
          </div>
        </div>
        <div className="auth-field-group">
          <label className="auth-field-label" htmlFor="nom">Nom <span>*</span></label>
          <div className="auth-field-wrap">
            <input type="text" id="nom" placeholder="Dupont" value={form.nom} onChange={upd("nom")} required/>
            <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>
          </div>
        </div>
      </div>

      <div className="auth-field-group">
        <label className="auth-field-label" htmlFor="email">Adresse email <span>*</span></label>
        <div className="auth-field-wrap">
          <input type="email" id="email" placeholder="jean.dupont@email.com" autoComplete="email" value={form.email} onChange={upd("email")} required data-testid="register-email"/>
          <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 7L2 7"/></svg></div>
        </div>
      </div>

      <div className="auth-field-group">
        <label className="auth-field-label" htmlFor="tel">Téléphone</label>
        <div className="auth-field-wrap">
          <input type="tel" id="tel" placeholder="+33 6 XX XX XX XX" value={form.tel} onChange={upd("tel")}/>
          <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 17l.01-.08z"/></svg></div>
        </div>
      </div>

      <div className="auth-field-group">
        <label className="auth-field-label" htmlFor="adresse">Adresse</label>
        <div className="auth-field-wrap">
          <input type="text" id="adresse" placeholder="12 rue de la République" value={form.adresse} onChange={upd("adresse")}/>
          <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>
        </div>
      </div>

      <div className="auth-grid-2">
        <div className="auth-field-group">
          <label className="auth-field-label" htmlFor="cp">Code postal</label>
          <div className="auth-field-wrap">
            <input type="text" id="cp" placeholder="13001" value={form.cp} onChange={upd("cp")}/>
            <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg></div>
          </div>
        </div>
        <div className="auth-field-group">
          <label className="auth-field-label" htmlFor="ville">Ville</label>
          <div className="auth-field-wrap">
            <input type="text" id="ville" placeholder="Marseille" value={form.ville} onChange={upd("ville")}/>
            <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>
          </div>
        </div>
      </div>

      <div className="auth-field-group">
        <label className="auth-field-label" htmlFor="f_pwd">Mot de passe <span>*</span></label>
        <div className="auth-field-wrap">
          <input type={pwdVisible?"text":"password"} id="f_pwd" placeholder="8 caractères minimum"
            value={form.pwd} onChange={upd("pwd")} data-testid="register-password" required/>
          <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></div>
          <button type="button" className="auth-pwd-toggle" onClick={() => setPwdVisible(v=>!v)}>
            {pwdVisible ? <IcEyeOff/> : <IcEye/>}
          </button>
        </div>
      </div>
      <div className="auth-field-group">
        <label className="auth-field-label" htmlFor="f_pwd2">Confirmer le mot de passe <span>*</span></label>
        <div className="auth-field-wrap">
          <input type={pwdVisible?"text":"password"} id="f_pwd2" placeholder="Répétez votre mot de passe"
            value={form.pwd2} onChange={upd("pwd2")} required/>
          <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></div>
        </div>
      </div>

      {profile === "pro" && (
        <>
          <div className="auth-section-sep"/>
          <div className="auth-section-label">Structure juridique</div>
          <div className="auth-field-group">
            <label className="auth-field-label" htmlFor="forme">Forme juridique</label>
            <div className="auth-field-wrap">
              <select id="forme" value={form.forme} onChange={upd("forme")}>
                <option value="">Choisir…</option>
                {["Auto-entrepreneur","SARL","SAS","SA","Entreprise individuelle"].map(o=><option key={o} value={o}>{o}</option>)}
              </select>
              <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="4" y="2" width="16" height="20" rx="1"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01M12 14h.01"/></svg></div>
            </div>
          </div>
          <div className="auth-grid-2">
            <div className="auth-field-group">
              <label className="auth-field-label" htmlFor="siret">SIRET <span>*</span></label>
              <div className="auth-field-wrap">
                <input type="text" id="siret" placeholder="XXX XXX XXX XXXXX" value={form.siret} onChange={upd("siret")}/>
                <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="4" y="2" width="16" height="20" rx="1"/></svg></div>
              </div>
            </div>
            <div className="auth-field-group">
              <label className="auth-field-label" htmlFor="raison">Raison sociale</label>
              <div className="auth-field-wrap">
                <input type="text" id="raison" placeholder="Transport Dupont SARL" value={form.raison} onChange={upd("raison")}/>
                <div className="auth-field-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="4" y="2" width="16" height="20" rx="1"/></svg></div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="auth-btn-row">
        <button className="auth-btn-back" onClick={() => { setError(""); setStep(0); }}><IcBack/> Retour</button>
        <button className="auth-btn-submit" onClick={validateStep1} data-testid="next-step-1">
          <span>{profile === "pro" ? "Documents" : "Validation"}</span><div className="auth-btn-arrow"><IcArrow/></div>
        </button>
      </div>
    </div>
  );

  // ── Étape 2 : documents
  const renderStep2 = () => (
    <div className="auth-pane">
      <div className="auth-step-header">
        <div className="auth-step-tag"><span className="auth-step-tag-dot"/>Étape 3 sur 4</div>
        <h2 className="auth-step-title">Pièces justificatives</h2>
        <p className="auth-step-sub">
          Cliquez sur chaque zone pour charger le document. Vos pièces seront transmises
          à votre espace Conformité et vérifiées par notre équipe.
        </p>
      </div>

      {error && <div className="auth-alert auth-alert-error"><IcWarn/> {error}</div>}

      <div className="auth-section-label">Documents obligatoires ({reqDocs.filter(d=>uploaded[d.id]).length}/{reqDocs.length})</div>

      {reqDocs.map(doc => (
        <label key={doc.id} className={`auth-doc-zone${uploaded[doc.id]?" doc-uploaded":""}${doc.missing?" doc-missing":""}` }>
          <input type="file" className="auth-doc-input" accept="image/*,application/pdf" onChange={e => handleFileChange(doc.id, e)}/>
          <div className="auth-doc-left">
            <div className="auth-doc-name">{doc.name}</div>
            <div className="auth-doc-sub">{doc.sub}</div>
            {uploaded[doc.id] && <div className="auth-doc-file"><IcFile/> {uploaded[doc.id].name}</div>}
            {doc.expiry && !uploaded[doc.id] && <div className="auth-doc-expiry"><IcClock/> Date d’expiration requise lors du dépôt</div>}
          </div>
          {uploaded[doc.id]
            ? <span className="auth-badge auth-badge-ok"><IcCheck/> Chargé</span>
            : <span className="auth-badge auth-badge-req"><IcUpload/> Requis</span>
          }
        </label>
      ))}

      {condDocs.length > 0 && (
        <>
          <div className="auth-section-label" style={{marginTop:"1.25rem"}}>Documents conditionnels</div>
          {condDocs.map(doc => (
            <label key={doc.id} className={`auth-doc-zone${uploaded[doc.id]?" doc-uploaded":""}`}>
              <input type="file" className="auth-doc-input" accept="image/*,application/pdf" onChange={e => handleFileChange(doc.id, e)}/>
              <div className="auth-doc-left">
                <div className="auth-doc-name">{doc.name}</div>
                <div className="auth-doc-sub">{doc.sub}</div>
                {uploaded[doc.id] && <div className="auth-doc-file"><IcFile/> {uploaded[doc.id].name}</div>}
              </div>
              {uploaded[doc.id]
                ? <span className="auth-badge auth-badge-ok"><IcCheck/> Chargé</span>
                : <span className="auth-badge" style={{background:"#E6F1FB",color:"#185FA5"}}>Conditionnel</span>
              }
            </label>
          ))}
        </>
      )}

      <div className="auth-info-box">
        <IcShield/>
        <span>Chaque document est scellé par empreinte SHA-256 et horodaté au dépôt ;
        il n'est visible que de vous et de l'équipe de vérification.</span>
      </div>

      <div className="auth-btn-row">
        <button className="auth-btn-back" onClick={() => { setError(""); setStep(1); }}><IcBack/> Retour</button>
        <button className="auth-btn-submit" onClick={() => { if(!allDocsOk){setError("Veuillez charger tous les documents obligatoires.");return;} setError("");setStep(3); }} disabled={!allDocsOk} data-testid="next-step-2">
          <span>Validation</span><div className="auth-btn-arrow"><IcArrow/></div>
        </button>
      </div>
    </div>
  );

  // ── Étape 3 : validation (récapitulatif + CGU)
  const renderStep3 = () => (
    <div className="auth-pane">
      <div className="auth-step-header">
        <div className="auth-step-tag"><span className="auth-step-tag-dot"/>Étape {profile==="pro" ? "4 sur 4" : "3 sur 3"}</div>
        <h2 className="auth-step-title">Validation de votre inscription</h2>
        <p className="auth-step-sub">Vérifiez votre récapitulatif puis acceptez les conditions.</p>
      </div>

      {error && <div className="auth-alert auth-alert-error"><IcWarn/> {error}</div>}

      <div className="auth-receipt">
        <div className="auth-receipt-row"><span className="auth-receipt-key">Profil</span><span className="auth-receipt-val">{profile==="pro"?"Transporteur professionnel":"Client particulier"}</span></div>
        <div className="auth-receipt-row"><span className="auth-receipt-key">Nom complet</span><span className="auth-receipt-val">{[form.prenom,form.nom].filter(Boolean).join(" ")||"—"}</span></div>
        <div className="auth-receipt-row"><span className="auth-receipt-key">Email</span><span className="auth-receipt-val">{form.email||"—"}</span></div>
        {profile==="pro" && (
          <>
            <div className="auth-receipt-row"><span className="auth-receipt-key">Société</span><span className="auth-receipt-val">{form.raison||"—"}{form.siret ? ` · SIRET ${form.siret}` : ""}</span></div>
            <div className="auth-receipt-row"><span className="auth-receipt-key">Documents à transmettre</span><span className="auth-receipt-val">{Object.keys(uploaded).length} fichier(s)</span></div>
          </>
        )}
      </div>

      <div className="auth-cgu-box">
        <input type="checkbox" id="cgu" checked={cguChecked} onChange={e=>setCguChecked(e.target.checked)}/>
        <div className="auth-cgu-text">
          J'accepte les <a href="/cgu" target="_blank" rel="noreferrer">Conditions Générales d'Utilisation</a> et
          la <a href="/confidentialite" target="_blank" rel="noreferrer">Politique de confidentialité</a> de DepannRoute.
        </div>
      </div>

      <div className="auth-btn-row">
        <button className="auth-btn-back" onClick={() => { setError(""); setStep(profile==="pro" ? 2 : 1); }}><IcBack/> Retour</button>
        <button className="auth-btn-submit" onClick={finalSubmit}
          disabled={!cguChecked || loading} data-testid="register-submit">
          {loading
            ? <><span className="auth-spinner"/><span>Création…</span></>
            : <><span>Valider mon inscription</span><div className="auth-btn-arrow"><IcCheck/></div></>
          }
        </button>
      </div>
    </div>
  );

  // ── Success
  const renderSuccess = () => (
    <div className="auth-pane auth-success-screen">
      <div className="auth-success-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <h2 className="auth-success-title">{profile==="pro" ? "Inscription soumise !" : "Compte créé !"}</h2>
      <p className="auth-success-sub">
        {profile==="pro"
          ? "Vos documents sont en cours de vérification par notre équipe. Vous pouvez suivre leur statut (et compléter votre dossier) depuis votre espace Conformité."
          : "Bienvenue ! Vous pouvez publier votre première demande de transport dès maintenant."}
      </p>
      {docErrors > 0 && (
        <div className="auth-alert auth-alert-error" style={{maxWidth:420,margin:"0 auto 1rem"}}>
          <IcWarn/> {docErrors} document(s) n'ont pas pu être transmis — renvoyez-les depuis votre espace Conformité.
        </div>
      )}
      <div className="auth-receipt">
        <div className="auth-receipt-row"><span className="auth-receipt-key">Profil</span><span className="auth-receipt-val">{profile==="pro"?"Transporteur professionnel":"Client particulier"}</span></div>
        <div className="auth-receipt-row"><span className="auth-receipt-key">Nom complet</span><span className="auth-receipt-val">{[form.prenom,form.nom].filter(Boolean).join(" ")||"—"}</span></div>
        <div className="auth-receipt-row"><span className="auth-receipt-key">Email</span><span className="auth-receipt-val">{form.email||"—"}</span></div>
        {profile==="pro" && (
          <div className="auth-receipt-row"><span className="auth-receipt-key">Documents transmis (scellés SHA-256)</span><span className="auth-receipt-val rval-ok">{docsSent} fichier(s)</span></div>
        )}
        <div className="auth-receipt-row"><span className="auth-receipt-key">Horodatage</span><span className="auth-receipt-val rval-ok">{new Date().toLocaleString("fr-FR")}</span></div>
      </div>
      <button className="auth-btn-submit" style={{maxWidth:240,margin:"0 auto"}} onClick={() => navigate("/dashboard",{replace:true})} data-testid="go-dashboard">
        <span>Aller au tableau de bord</span><div className="auth-btn-arrow"><IcArrow/></div>
      </button>
    </div>
  );

  // ── Render principal
  return (
    <div className="auth-layout" data-testid="register-page">
      <LeftPanel step={step >= 4 ? 4 : step} />
      <div className="auth-right">
        <div className="auth-form-wrap">
          <div className="auth-progress-track">
            <div className="auth-progress-fill" style={{width: pct+"%"}}/>
          </div>
          {step === 0 && renderStep0()}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderSuccess()}
          {step < 4 && (
            <p className="auth-bottom-link">
              Déjà un compte ? <Link to="/login">Se connecter →</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
